import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Notif {
  id: number;
  titulo: string;
  texto: string | null;
  actorNombre: string | null;
  createdAt: string;
  leidaAt: string | null;
  bookingId: number | null;
  booking?: { bookingIdExterno: string } | null;
}

function tiempoRelativo(iso: string): string {
  const min = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 60000));
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d === 1 ? '' : 's'}`;
}

/**
 * Campanita de notificaciones in-app (14-sep-2026, estilo Odoo).
 * Dos pestañas: "Sin leer" y "Completadas" (se muestran 7 días).
 * - Click en la notificación → navega al embarque en el board, expandido con
 *   sus tareas del workflow (5-oct-2026).
 * - El checkbox marca la notificación como completada (antes era una X); en
 *   "Completadas" se muestra ya palomeado.
 */
export function NotifBell() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'sinLeer' | 'completadas'>('sinLeer');
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['notificaciones'],
    queryFn: () => api<{ sinLeer: Notif[]; anteriores: Notif[] }>('/notificaciones'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const marcarLeida = useMutation({
    mutationFn: (id: number) => api(`/notificaciones/${id}/leida`, { method: 'PATCH' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notificaciones'] }),
  });
  const marcarNoLeida = useMutation({
    mutationFn: (id: number) => api(`/notificaciones/${id}/no-leida`, { method: 'PATCH' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notificaciones'] }),
  });
  const marcarTodas = useMutation({
    mutationFn: () => api('/notificaciones/leidas', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notificaciones'] }),
  });

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const sinLeer = q.data?.sinLeer ?? [];
  const completadas = q.data?.anteriores ?? [];
  const lista = tab === 'sinLeer' ? sinLeer : completadas;

  const irAlEmbarque = (n: Notif) => {
    if (!n.booking?.bookingIdExterno) return;
    setOpen(false);
    // el board abre el embarque expandido con sus tareas del workflow
    navigate(`/board?focus=${encodeURIComponent(n.booking.bookingIdExterno)}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md text-ink-500 hover:text-ink-900 hover:bg-surface-2"
        aria-label="Notificaciones"
        title="Notificaciones"
      >
        <Bell className="h-5 w-5" />
        {sinLeer.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {sinLeer.length > 99 ? '99+' : sinLeer.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[92vw] rounded-lg border border-ink-200 bg-surface-1 shadow-xl z-50">
          <div className="flex items-center border-b border-ink-100">
            {(['sinLeer', 'completadas'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  'flex-1 px-3 py-2 text-sm font-medium border-b-2',
                  tab === t
                    ? 'border-brand-500 text-ink-900'
                    : 'border-transparent text-ink-400 hover:text-ink-700',
                )}
              >
                {t === 'sinLeer' ? `Sin leer (${sinLeer.length})` : 'Completadas'}
              </button>
            ))}
            {tab === 'sinLeer' && sinLeer.length > 0 && (
              <button
                onClick={() => marcarTodas.mutate()}
                className="px-3 py-2 text-xs text-brand-600 hover:underline whitespace-nowrap"
              >
                Marcar todas
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {lista.length === 0 && (
              <p className="px-4 py-6 text-sm text-ink-400 text-center">
                {tab === 'sinLeer' ? 'Sin notificaciones nuevas 🎉' : 'Nada en los últimos 7 días'}
              </p>
            )}
            {lista.map((n) => {
              const navegable = !!n.booking?.bookingIdExterno;
              return (
                <div
                  key={n.id}
                  className={cn(
                    'flex items-start gap-2 px-4 py-2.5 border-b border-ink-50',
                    tab === 'sinLeer' ? 'bg-surface-1' : 'bg-surface-2/50 opacity-75',
                  )}
                >
                  {/* checkbox: marca como completada; en "Completadas" ya va palomeado */}
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-brand-500 shrink-0 cursor-pointer"
                    checked={tab !== 'sinLeer'}
                    disabled={marcarLeida.isPending || marcarNoLeida.isPending}
                    onChange={() =>
                      tab === 'sinLeer' ? marcarLeida.mutate(n.id) : marcarNoLeida.mutate(n.id)
                    }
                    title={
                      tab === 'sinLeer'
                        ? 'Marcar como completada'
                        : 'Quitar el check (vuelve a Sin leer)'
                    }
                  />
                  <div
                    className={cn('flex-1 min-w-0', navegable && 'cursor-pointer group')}
                    onClick={() => irAlEmbarque(n)}
                    title={
                      navegable
                        ? `Ir al embarque ${n.booking!.bookingIdExterno} y sus tareas pendientes`
                        : undefined
                    }
                  >
                    <p
                      className={cn(
                        'text-sm font-medium text-ink-900 leading-snug',
                        navegable && 'group-hover:text-brand-600 group-hover:underline',
                      )}
                    >
                      {n.titulo}
                    </p>
                    {n.texto && (
                      <p className="text-xs text-ink-500 mt-0.5 line-clamp-2">
                        {n.actorNombre ? `${n.actorNombre}: ` : ''}
                        {n.texto}
                      </p>
                    )}
                    <p className="text-[11px] text-ink-400 mt-0.5">{tiempoRelativo(n.createdAt)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
